import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { useUpdateRecurringCost } from "@/features/finances/hooks/useFinances";
import type { InternalCostRecurring } from "@/features/finances/types";

interface ProjectOption { id: string; short_name: string; name: string }
interface CompanyOption { id: string; short_code: string; name: string }

interface EditRecurringCostModalProps {
  cost: InternalCostRecurring;
  projects: ProjectOption[];
  companies: CompanyOption[];
  onClose: () => void;
}

const OPEN_ENDED = "2099-12-31";

/** Edit a recurring cost's editable fields: opis, przedsięwzięcie, spółka, dzień, od–do. */
export function EditRecurringCostModal({ cost, projects, companies, onClose }: EditRecurringCostModalProps) {
  const [description, setDescription] = useState(cost.description);
  const [project, setProject] = useState(cost.project ?? "");
  const [company, setCompany] = useState(cost.company ?? "");
  const [day, setDay] = useState(String(cost.day_of_month));
  // Domyślna średnia — edytowalna TYLKO, dopóki koszt nie ma żadnej liczonej
  // faktury. Z fakturami średnia wynika z ich kwot i ręczna wartość jest martwa
  // (backend też to odrzuca), więc pola wtedy w ogóle nie pokazujemy.
  const hasInvoices = cost.expense_invoices.some((f) => f.status !== "rejected");
  const [defaultAvg, setDefaultAvg] = useState(cost.amount_monthly ?? "");
  const [startDate, setStartDate] = useState(cost.start_date);
  const hasEnd = !!cost.end_date && cost.end_date < OPEN_ENDED;
  const [endEnabled, setEndEnabled] = useState(hasEnd);
  const [endDate, setEndDate] = useState(hasEnd ? cost.end_date! : "");
  const [err, setErr] = useState<string | null>(null);

  const updateRC = useUpdateRecurringCost();

  const save = async () => {
    if (!description.trim() || !startDate) {
      setErr("Opis i data startu są wymagane.");
      return;
    }
    const d = parseInt(day, 10);
    if (isNaN(d) || d < 1 || d > 31) {
      setErr("Dzień miesiąca musi być 1–31.");
      return;
    }
    const avg = defaultAvg.trim().replace(",", ".");
    if (!hasInvoices && avg && (isNaN(Number(avg)) || Number(avg) < 0)) {
      setErr("Domyślna średnia musi być kwotą (PLN).");
      return;
    }
    try {
      await updateRC.mutateAsync({
        id: cost.id,
        data: {
          description: description.trim(),
          project: project || null,
          company: company || null,
          day_of_month: d,
          start_date: startDate,
          end_date: endEnabled ? (endDate || OPEN_ENDED) : OPEN_ENDED,
          ...(hasInvoices ? {} : { amount_monthly: avg || null }),
        },
      });
      onClose();
    } catch {
      setErr("Błąd zapisu.");
    }
  };

  const inputCls = "w-full border border-gray-300 rounded px-2 py-1.5 text-sm";

  return (
    <Modal open onClose={onClose} title="Edytuj koszt stały">
      <div className="flex flex-col gap-3">
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis *</label>
          <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Przedsięwzięcie</label>
          <select className={inputCls} value={project} onChange={(e) => setProject(e.target.value)}>
            <option value="">— bez przypisania —</option>
            {[...projects].sort((a, b) => a.short_name.localeCompare(b.short_name)).map((p) => (
              <option key={p.id} value={p.id}>{p.short_name} — {p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Spółka GK</label>
          <select className={inputCls} value={company} onChange={(e) => setCompany(e.target.value)}>
            <option value="">— bez przypisania —</option>
            {[...companies].sort((a, b) => a.short_code.localeCompare(b.short_code)).map((c) => (
              <option key={c.id} value={c.id}>{c.short_code} — {c.name}</option>
            ))}
          </select>
        </div>
        {!hasInvoices && (
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Domyślna średnia (PLN/mies.)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={inputCls}
              value={defaultAvg}
              onChange={(e) => setDefaultAvg(e.target.value)}
              placeholder="0,00"
            />
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
              Używana w prognozach, dopóki nie wpłynie pierwsza faktura — potem
              średnia liczy się z kwot na fakturach i to pole znika.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Dzień miesiąca *</label>
            <input type="number" min="1" max="31" className={inputCls} value={day} onChange={(e) => setDay(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data startu *</label>
            <DatePicker value={startDate} onChange={setStartDate} />
          </div>
        </div>
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-1">
            <input
              type="checkbox"
              checked={endEnabled}
              onChange={(e) => setEndEnabled(e.target.checked)}
              className="w-4 h-4 accent-[var(--color-primary)]"
            />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Data końcowa (inaczej bezterminowo)</span>
          </label>
          {endEnabled && (
            <DatePicker value={endDate} onChange={setEndDate} />
          )}
        </div>
        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex gap-2">
          <Button size="sm" loading={updateRC.isPending} onClick={save}>Zapisz</Button>
          <Button size="sm" variant="secondary" onClick={onClose}>Anuluj</Button>
        </div>
      </div>
    </Modal>
  );
}
